import React, { useMemo, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { toast } from 'react-toastify'

import { Company } from '../../../models'
import { OrganizationVendor } from '../../../hooks/useOrganizationManagement'

interface OrganizationVendorPropertiesDialogProps {
    open: boolean
    onClose: () => void
    vendor: OrganizationVendor | null
    allProperties: Company[]
    onAddProperty: (vendorId: number, property: Company) => Promise<void> | void
    onRemoveProperty: (
        vendorId: number,
        propertyId: number,
    ) => Promise<void> | void
}

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        height: '80vh',
        maxHeight: '80vh',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    sectionTitle: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    vendorSummary: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1, 2),
        marginBottom: theme.spacing(2),
    },
    addControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    selectControl: {
        minWidth: 280,
    },
    propertiesTableContainer: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
    },
}))

export const OrganizationVendorPropertiesDialog = (
    props: OrganizationVendorPropertiesDialogProps,
) => {
    const {
        open,
        onClose,
        vendor,
        allProperties,
        onAddProperty,
        onRemoveProperty,
    } = props
    const classes = useStyles()
    const [selectedPropertyId, setSelectedPropertyId] = useState<number | ''>(
        '',
    )
    const [isSaving, setIsSaving] = useState<boolean>(false)

    const sortedProperties = useMemo(() => {
        if (!vendor) return []
        return [...vendor.properties].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        )
    }, [vendor])

    const availableProperties = useMemo(() => {
        if (!vendor) return []
        const assignedIds = new Set(
            vendor.properties.map((property) => property.id),
        )
        return allProperties
            .filter((property) => !assignedIds.has(property.id))
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                    sensitivity: 'base',
                }),
            )
    }, [allProperties, vendor])

    const selectedProperty =
        selectedPropertyId === ''
            ? null
            : availableProperties.find(
                  (property) => property.id === selectedPropertyId,
              ) || null

    const handleAddProperty = async () => {
        if (!vendor || !selectedProperty) return
        try {
            setIsSaving(true)
            await onAddProperty(vendor.id, selectedProperty)
            setSelectedPropertyId('')
        } catch {
            toast.error('Unable to add vendor to property.')
        } finally {
            setIsSaving(false)
        }
    }

    const vendorAddress = vendor?.primary_address
        ? `${vendor.primary_address.address1}, ${vendor.primary_address.city}, ${vendor.primary_address.state} ${vendor.primary_address.zip_code}`
        : 'N/A'

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogTitle>Manage Properties</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Typography className={classes.sectionTitle}>
                    Vendor Details
                </Typography>
                <div className={classes.vendorSummary}>
                    <Typography variant="body2">
                        <strong>Name:</strong> {vendor?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Email:</strong> {vendor?.email || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Invoice Code:</strong>{' '}
                        {vendor?.invoice_code || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Type:</strong>{' '}
                        {vendor?.in_house ? 'In House' : 'Contractor'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Address:</strong> {vendorAddress}
                    </Typography>
                </div>

                <Typography className={classes.sectionTitle}>
                    Add Vendor to Property
                </Typography>
                <div className={classes.addControls}>
                    <FormControl
                        variant="outlined"
                        size="small"
                        className={classes.selectControl}
                    >
                        <InputLabel id="add-property-select-label">
                            Property
                        </InputLabel>
                        <Select
                            labelId="add-property-select-label"
                            value={selectedPropertyId}
                            onChange={(event) =>
                                setSelectedPropertyId(
                                    event.target.value as number | '',
                                )
                            }
                            label="Property"
                        >
                            {availableProperties.map((property) => (
                                <MenuItem key={property.id} value={property.id}>
                                    {property.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddProperty}
                        disabled={!vendor || !selectedProperty || isSaving}
                    >
                        Add Vendor to Property
                    </Button>
                </div>

                <Typography className={classes.sectionTitle}>
                    Properties
                </Typography>
                <TableContainer className={classes.propertiesTableContainer}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">Remove</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedProperties.map((property) => (
                                <TableRow key={property.id}>
                                    <TableCell>{property.name}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                vendor &&
                                                onRemoveProperty(
                                                    vendor.id,
                                                    property.id,
                                                )
                                            }
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sortedProperties.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Typography variant="body2">
                                            No properties currently assigned.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}
